import { Calendar as PrimeCalendar } from 'primereact/calendar';

const MASCARA_DATA = '99/99/9999';

/**
 * Completa uma data digitada parcialmente na máscara dd/mm/aaaa:
 * - 25/10/26 -> 25/10/2026 (ano com 2 dígitos: menor que 60 vira 20xx, senão 19xx)
 * - 25/10    -> 25/10/<ano atual>
 * - 25       -> 25/<mês atual>/<ano atual>
 * Dia ou mês com um dígito recebem zero à esquerda (5 -> 05).
 * Retorna null quando não há o que completar (campo vazio, data já completa ou formato não reconhecido).
 */
export function completarData(texto) {
  if (!texto) return null;

  const partes = texto.split('/').map(parte => parte.replace(/[^0-9]/g, ''));
  const [dia = '', mes = '', ano = ''] = partes;

  if (!dia) return null;
  if (dia.length === 2 && mes.length === 2 && ano.length === 4) return null; // já completa
  if (!mes && ano) return null; // ano sem mês: não completa

  const hoje = new Date();
  let anoCompleto;

  if (ano.length === 4) {
    anoCompleto = ano;
  } else if (ano.length === 2) {
    const anoNumero = Number(ano);
    anoCompleto = String(anoNumero < 60 ? 2000 + anoNumero : 1900 + anoNumero);
  } else if (ano.length === 0) {
    anoCompleto = String(hoje.getFullYear());
  } else {
    return null; // ano com 1 ou 3 dígitos: deixa o comportamento padrão
  }

  const mesCompleto = mes ? mes.padStart(2, '0') : String(hoje.getMonth() + 1).padStart(2, '0');
  const diaCompleto = dia.padStart(2, '0');

  return `${diaCompleto}/${mesCompleto}/${anoCompleto}`;
}

/**
 * Calendar do PrimeReact com duas melhorias para a máscara 99/99/9999:
 *
 * 1) Correção de digitação: no PrimeReact 7.2.1, quando o campo já tem uma data e o usuário
 *    seleciona o texto e digita outra, o Calendar limpava o campo no primeiro dígito e a data
 *    ficava errada (ex.: "52/__/____" ao digitar 25/10/2026).
 *
 * 2) Data abreviada: ao sair do campo (Tab, clique fora) ou teclar Enter, completa a data
 *    digitada pela metade — 25/10/26, 25/10 ou só 25 (ver completarData).
 */
export class Calendar extends PrimeCalendar {
  componentDidMount() {
    // Registrado antes da máscara (super.componentDidMount), para completar a data
    // antes que a máscara limpe o valor incompleto no blur/Enter.
    const input = this.inputRef && this.inputRef.current;
    if (input && this.props.mask === MASCARA_DATA) {
      this.completarDataNoBlur = () => this.completarValorDigitado();
      this.completarDataNoEnter = (e) => {
        if ((e.which || e.keyCode) === 13) this.completarValorDigitado();
      };
      input.addEventListener('blur', this.completarDataNoBlur);
      input.addEventListener('keydown', this.completarDataNoEnter);
    }

    super.componentDidMount();
  }

  componentWillUnmount() {
    const input = this.inputRef && this.inputRef.current;
    if (input && this.completarDataNoBlur) {
      input.removeEventListener('blur', this.completarDataNoBlur);
      input.removeEventListener('keydown', this.completarDataNoEnter);
    }

    super.componentWillUnmount();
  }

  completarValorDigitado() {
    const input = this.inputRef && this.inputRef.current;
    if (!input || this.props.disabled || this.props.readOnlyInput) return;

    const dataCompleta = completarData(input.value);
    if (dataCompleta) {
      input.value = dataCompleta;
    }
  }

  updateValueOnInput(event, rawValue) {
    this.digitandoNaMascara = true;
    try {
      super.updateValueOnInput(event, rawValue);
    } finally {
      setTimeout(() => {
        this.digitandoNaMascara = false;
      }, 0);
    }
  }

  updateInputfield(value) {
    if (this.digitandoNaMascara && !value && this.props.mask) {
      return;
    }
    super.updateInputfield(value);
  }
}

export default Calendar;
